import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import CanonicalTag from "@/components/CanonicalTag";

const CraftsmanADUDesignRedwoodCity = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Craftsman ADU Design & Construction in Redwood City",
    "description": "Custom Craftsman ADUs in Redwood City. Expert design & construction by UDA ADU. Get your free consultation today.",
    "publisher": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://www.udaadu.com"
    },
    "url": "https://www.udaadu.com/craftsman-adu-design-redwood-city"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/craftsman-adu-design-redwood-city" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }} />
      
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button onClick={closeCalendlyModal} className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors">
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" width="100%" height="100%" frameBorder="0" title="Schedule a meeting" />
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <section className="text-center py-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white">
            Craftsman ADU Design & Construction in Redwood City
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto text-gray-300 mb-8">
            Build exceptional Craftsman ADUs in Redwood City that honor the Peninsula's rich architectural heritage with authentic handcrafted details and modern convenience.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Get Free Consultation
          </Button>
        </section>

        {/* Why Choose Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Why Choose Craftsman ADUs in Redwood City?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Historic Peninsula Character</h3>
              <p className="text-gray-300">Craftsman design celebrates Redwood City's role in Peninsula history, creating ADUs that honor the community's architectural legacy and natural beauty.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Urban Village Charm</h3>
              <p className="text-gray-300">Authentic craftsmanship that enhances Redwood City's urban village atmosphere, blending neighborhood character with contemporary functionality.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Transit-Oriented Living</h3>
              <p className="text-gray-300">Craftsman ADUs provide walkable, sustainable living options perfect for Redwood City's transit-friendly downtown and neighborhoods.</p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Craftsman ADU Features & Design Elements
          </h2>
          <div className="bg-white rounded-xl p-8 max-w-4xl mx-auto">
            <ul className="space-y-4 text-gray-800">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Traditional post-and-beam construction with visible joinery</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Custom cabinetry and built-in storage for urban efficiency</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Welcoming porches and outdoor living spaces</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Natural materials sourced from sustainable Peninsula suppliers</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Energy-efficient systems integrated with craftsman aesthetics</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Multi-functional spaces optimized for urban living</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Local Considerations */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Redwood City-Specific Craftsman ADU Considerations
          </h2>
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8 max-w-4xl mx-auto">
            <p className="text-gray-300 mb-6">
              Building Craftsman ADUs in Redwood City requires understanding of urban development patterns and community character:
            </p>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Redwood City's downtown corridor and neighborhood design standards</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Historic district preservation and architectural compatibility</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>San Mateo County building codes and urban infill requirements</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Transit-oriented development guidelines and parking standards</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#e4683b] mr-3">•</span>
                <span>Urban forest preservation and streetscape enhancement</span>
              </li>
            </ul>
            <p className="text-gray-300 mt-6">
              Our urban design expertise ensures your Craftsman ADU enhances Redwood City's walkable neighborhoods while meeting all regulatory requirements.
            </p>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            What to Expect: Craftsman ADU Design Process
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Urban-Sensitive Design</h3>
              <p className="text-gray-300">We create Craftsman designs that maximize urban lots while preserving neighborhood character and enhancing community livability.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Streamlined Permitting</h3>
              <p className="text-gray-300">Our team efficiently navigates Redwood City's permitting process with expertise in urban development and historic preservation requirements.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Quality Craftsmanship</h3>
              <p className="text-gray-300">Experienced craftsmen execute traditional techniques adapted for urban construction, delivering authentic Craftsman character and durability.</p>
            </div>
            <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-medium text-[#e4683b] mb-4">Community Enhancement</h3>
              <p className="text-gray-300">From planning to completion, we deliver Craftsman ADUs that strengthen Redwood City's neighborhoods and property values.</p>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="py-16">
          <h2 className="text-3xl md:text-4xl font-light text-white text-center mb-12">
            Craftsman ADU Cost in Redwood City
          </h2>
          <div className="text-center">
            <Link to="/adu-calculator">
              <Button className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-6 px-12 rounded-xl shadow-lg text-xl">
                Use Our ADU Cost Calculator
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
            Ready to Build Your Craftsman ADU in Redwood City?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Create a handcrafted ADU that honors Redwood City's Peninsula heritage while embracing the community's vision for sustainable urban living.
          </p>
          <Button onClick={openCalendlyModal} className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-4 px-8 rounded-xl shadow-lg transition-colors text-lg">
            Book Free Consultation
          </Button>
        </section>

        {/* Internal Links */}
        <section className="py-16">
          <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8">
            <h3 className="text-2xl font-light text-white mb-6">Explore More ADU Options in Redwood City:</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <Link to="/redwood-city-adu" className="text-[#FF7A00] hover:underline">
                General ADU Services in Redwood City
              </Link>
              <Link to="/adu-cost-redwood-city" className="text-[#FF7A00] hover:underline">
                ADU Cost in Redwood City
              </Link>
              <Link to="/adu-regulations-redwood-city" className="text-[#FF7A00] hover:underline">
                ADU Regulations in Redwood City
              </Link>
            </div>
            
            <h3 className="text-2xl font-light text-white mb-6">Other Craftsman ADU Locations:</h3>
            <div className="grid md:grid-cols-4 gap-4">
              <Link to="/craftsman-adu-design-san-mateo" className="text-[#FF7A00] hover:underline">
                Craftsman ADU San Mateo
              </Link>
              <Link to="/craftsman-adu-design-menlo-park" className="text-[#FF7A00] hover:underline">
                Craftsman ADU Menlo Park
              </Link>
              <Link to="/craftsman-adu-design-belmont" className="text-[#FF7A00] hover:underline">
                Craftsman ADU Belmont
              </Link>
              <Link to="/craftsman-adu-design-san-carlos" className="text-[#FF7A00] hover:underline">
                Craftsman ADU San Carlos
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </div>
  );
};

export default CraftsmanADUDesignRedwoodCity;